"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { ImageUploadTabs } from "@shared/components/ImageUploadTabs";
import { useRouter } from "@shared/hooks/router";
import { Button } from "@ui/components/button";
import { Card, CardContent } from "@ui/components/card";
import { Textarea } from "@ui/components/textarea";
import {
	AlignLeft,
	Box,
	Copy,
	FileText,
	Globe,
	History as HistoryIcon,
	Image as ImageIcon,
	Infinity as InfinityIcon,
	MessageSquare,
	PenTool,
	ScanText,
	Text,
	Users,
	X,
	Zap,
	ZoomIn,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LanguageSelector } from "../../image-to-prompt/components/LanguageSelector";
import { useAiDescribeImage } from "../hooks/use-ai-describe-image";
import type { PromptType } from "../types";

export function ImageDescriptionGenerator() {
	const t = useTranslations("aiDescribeImage.client.imageDescription");
	const [zoomImage, setZoomImage] = useState<string | null>(null);
	const [showCustomInput, setShowCustomInput] = useState<boolean>(false);
	const router = useRouter();
	const generatedDescriptionRef = useRef<HTMLDivElement>(null);
	const { user } = useSession();
	const {
		imagePreviewUrl,
		selectedPromptType,
		promptLanguage,
		generatedDescription,
		customQuestion,
		usesRemaining,
		hasUnlimitedCredits,
		isPending,
		handleImageSelected,
		handleGenerateDescription,
		handleGetMoreCredits,
		setSelectedPromptType,
		setPromptLanguage,
		setCustomQuestion,
	} = useAiDescribeImage();

	// Auto-scroll to generated content when description changes
	useEffect(() => {
		if (generatedDescription && generatedDescriptionRef.current) {
			generatedDescriptionRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [generatedDescription]);

	// Handle copying description
	const handleCopyDescription = () => {
		if (generatedDescription) {
			navigator.clipboard.writeText(generatedDescription);
			toast.success(t("descriptionCopied"));
		}
	};

	// Handle button click
	const handleButtonClick = () => {
		// Check if user is logged in
		if (!user) {
			// Trigger login modal instead of redirect
			if (typeof window !== "undefined") {
				window.dispatchEvent(
					new CustomEvent("show-login", {
						detail: { redirectTo: "/ai-describe-image" },
					}),
				);
			}
			return;
		}

		// User is logged in, continue generating prompt
		handleGenerateDescription();
	};

	// Prompt options
	const promptOptions = [
		{
			id: "detail" as PromptType,
			icon: FileText,
			label: t("describeDetailed"),
		},
		{
			id: "brief" as PromptType,
			icon: AlignLeft,
			label: t("describeBrief"),
		},
		{
			id: "person" as PromptType,
			icon: Users,
			label: t("describePerson"),
		},
		{
			id: "objects" as PromptType,
			icon: Box,
			label: t("recognizeObjects"),
		},
		{
			id: "art-style" as PromptType,
			icon: PenTool,
			label: t("analyzeArtStyle"),
		},
		{
			id: "extract-text" as PromptType,
			icon: ScanText,
			label: t("extractText"),
		},
		{
			id: "custom" as PromptType,
			icon: MessageSquare,
			label: t("customQuestion"),
		},
	];

	// Toggle custom input
	const toggleCustomInput = (promptType: PromptType) => {
		if (promptType === "custom") {
			setShowCustomInput(true);
		} else {
			setShowCustomInput(false);
		}
	};

	return (
		<>
			<div>
				<div className="mb-4">
					<div className="flex flex-col md:flex-row gap-8">
						<div className="md:w-1/2">
							<Card className="h-full border border-indigo-200 hover:border-indigo-300 transition-colors shadow-sm">
								<CardContent className="p-4">
									<ImageUploadTabs
										onImageSelected={handleImageSelected}
									/>
								</CardContent>
							</Card>
						</div>

						<div className="md:w-1/2">
							<div className="rounded-lg border-2 border-solid border-slate-200 flex items-center justify-center h-[422px] max-h-[422px] bg-white overflow-hidden">
								{imagePreviewUrl ? (
									<div className="relative w-full h-full flex items-center justify-center group overflow-hidden">
										<img
											src={imagePreviewUrl}
											alt="Preview"
											className="max-w-full max-h-full object-contain"
										/>
										<div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
											<Button
												variant="ghost"
												size="icon"
												onClick={() =>
													handleImageSelected(
														null,
														"",
													)
												}
												className="bg-background/80 hover:bg-background shadow-sm backdrop-blur-sm"
												aria-label={t("removeImage")}
												type="button"
											>
												<X className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() =>
													setZoomImage(
														imagePreviewUrl,
													)
												}
												className="bg-background/80 hover:bg-background shadow-sm backdrop-blur-sm"
												title={t("zoomImage")}
											>
												<ZoomIn className="h-4 w-4" />
											</Button>
										</div>
									</div>
								) : (
									<div className="text-center">
										<ImageIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
										<p className="text-gray-400 font-medium mb-2">
											{t("yourImageWillAppear")}
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="mt-8 border rounded-xl p-5 bg-background/50 shadow-sm">
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h2 className="text-sm font-medium">
									{t("options")}
								</h2>
							</div>

							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
								{promptOptions.map((option) => (
									<button
										key={option.id}
										type="button"
										onClick={() => {
											setSelectedPromptType(option.id);
											toggleCustomInput(option.id);
										}}
										className={`flex items-center justify-start p-4 rounded-xl border transition-all ${
											selectedPromptType === option.id
												? "border-indigo-500 bg-indigo-50/40"
												: "border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/20"
										}`}
									>
										<option.icon className="h-5 w-5 text-indigo-500 mr-3 flex-shrink-0" />
										<span className="text-sm font-medium text-left">
											{option.label}
										</span>
									</button>
								))}
							</div>

							{showCustomInput && (
								<div className="w-full mt-4">
									<Textarea
										value={customQuestion}
										onChange={(e) =>
											setCustomQuestion(e.target.value)
										}
										placeholder={t(
											"customQuestionPlaceholder",
										)}
										className="min-h-[100px] border-slate-200 focus:border-indigo-500 rounded-md"
									/>
								</div>
							)}

							<div className="flex flex-col gap-4">
								<div className="flex items-center justify-between mt-6">
									<h3 className="text-sm font-medium">
										{t("promptLanguage")}
									</h3>
								</div>

								<div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-center">
									<div className="md:col-span-3">
										<LanguageSelector
											value={promptLanguage}
											onChange={setPromptLanguage}
										/>
									</div>
									<div className="md:col-span-9">
										<Button
											onClick={handleButtonClick}
											className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-xl font-medium flex justify-center gap-2"
											disabled={
												!imagePreviewUrl ||
												isPending ||
												(selectedPromptType ===
													"custom" &&
													!customQuestion)
											}
										>
											{isPending ? (
												<>
													<div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
													{t("generating")}
												</>
											) : (
												t("generateDescription")
											)}
										</Button>
									</div>
								</div>

								{user && (
									<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mt-4">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="text-sm px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
												Credits{" "}
												{typeof usesRemaining ===
													"string" &&
												usesRemaining === "infinity" ? (
													<>
														<InfinityIcon className="inline-block h-4 w-4 align-middle mx-1" />
													</>
												) : (
													<span>
														{Math.max(
															0,
															usesRemaining,
														)}
													</span>
												)}{" "}
												💎
											</span>
											<Button
												onClick={handleGetMoreCredits}
												variant="ghost"
												size="sm"
												className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
											>
												{t("getMore")}
											</Button>
										</div>
										<Button
											variant="ghost"
											size="sm"
											className="gap-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
											onClick={() => {
												// Open history dialog
												if (
													typeof window !==
													"undefined"
												) {
													window.dispatchEvent(
														new CustomEvent(
															"openDescriptionHistory",
														),
													);
												}
											}}
										>
											<HistoryIcon className="h-4 w-4" />
											{t("viewHistory")}
										</Button>
									</div>
								)}
							</div>
						</div>
					</div>

					{generatedDescription && (
						<div
							ref={generatedDescriptionRef}
							className="mt-8 border rounded-xl p-5 bg-background/50 shadow-sm"
						>
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<h2 className="text-base font-semibold text-gray-800 mb-1">
										{t("generatedDescription")}
									</h2>
									<Button
										variant="ghost"
										size="sm"
										className="gap-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
										onClick={handleCopyDescription}
									>
										<Copy className="h-4 w-4" />
										{t("copyDescription")}
									</Button>
								</div>

								<div className="p-4 bg-gray-50 rounded-lg border border-gray-100 whitespace-pre-wrap">
									{generatedDescription}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Zoom Image Modal */}
			{zoomImage && (
				<dialog
					className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 m-0 w-full h-full backdrop-blur-sm"
					onClick={() => setZoomImage(null)}
					onKeyUp={(e) => {
						if (e.key === "Escape") setZoomImage(null);
					}}
					open
				>
					<div className="relative max-w-5xl max-h-[90vh] w-full h-full">
						<img
							src={zoomImage}
							alt="Generated artwork in full view"
							className="w-full h-full object-contain"
						/>
						<button
							className="absolute top-2 right-2 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
							onClick={() => setZoomImage(null)}
							type="button"
							aria-label="Close"
						>
							<X className="h-6 w-6" />
						</button>
					</div>
				</dialog>
			)}
		</>
	);
}
